import React from "react";
import type { SubService } from "../../../types";
import { DataGrid, GridAction } from "../../shared";
import { Icons } from "../../shared/Icons";

interface SubServicesGridViewProps {
  subServices: SubService[];
  onEdit: (subService: SubService) => void;
  onDelete: (subServiceId: number) => void;
  onToggleStatus?: (subServiceId: number) => void;
  loading?: boolean;
}

export const SubServicesGridView: React.FC<SubServicesGridViewProps> = ({
  subServices,
  onEdit,
  onDelete,
  loading = false,
}) => {
  const renderSubServiceCard = (subService: SubService) => (
    <div
      className="bg-white dark:bg-slate-800 border-2 rounded-2xl shadow-medium hover:shadow-large 
              transition-all duration-300 transform hover:scale-[1.01] overflow-hidden 
              border-purple-200 dark:border-purple-800 hover:border-purple-300 dark:hover:border-purple-600"
    >
      {/* SubService Card Header */}
      <div className="p-6 border-b border-purple-200 dark:border-purple-800">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {subService.name}
            </h3>
            {subService.description && (
              <p className="text-sm text-gray-600 leading-relaxed">
                {subService.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={() => onEdit(subService)}
              disabled={loading}
              className="p-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg 
                     transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Editar Sub-serviço"
            >
              <Icons.Edit sx={{ fontSize: 20 }} />
            </button>
            <button
              onClick={() => onDelete(subService.id)}
              disabled={loading}
              className="p-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg 
                     transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Excluir Sub-serviço"
            >
              <Icons.Delete sx={{ fontSize: 20 }} />
            </button>
          </div>
        </div>
      </div>

      {/* SubService Card Footer */}
      <div className="p-6">
        <div className="flex flex-col items-center gap-1 text-center">
          <div className="text-sm font-medium text-gray-800">
            Validade:{" "}
            {subService.expirationDays != null
              ? `${subService.expirationDays} dias`
              : "—"}
          </div>
          <div className="text-xs text-gray-500">
            Criado em{" "}
            {subService.createdAt
              ? new Date(subService.createdAt).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "N/A"}
          </div>
        </div>
      </div>
    </div>
  );

  const actions: GridAction<SubService>[] = [
    {
      label: "Editar sub-serviço",
      icon: <Icons.Edit sx={{ fontSize: 18 }} />,
      onClick: (subService) => onEdit(subService),
      className:
        "p-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-lg transition-colors duration-200",
      title: "Editar sub-serviço",
      disabled: () => loading,
    },
    {
      label: "Excluir sub-serviço",
      icon: <Icons.Delete sx={{ fontSize: 18 }} />,
      onClick: (subService) => onDelete(subService.id),
      className:
        "p-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors duration-200",
      title: "Excluir sub-serviço",
      disabled: () => loading,
    },
  ];

  return (
    <DataGrid<SubService>
      data={subServices}
      renderCard={renderSubServiceCard}
      actions={actions}
      keyExtractor={(subService) => subService.id}
      emptyMessage="Nenhum sub-serviço encontrado"
      loading={loading}
      containerClassName="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
    />
  );
};
